// src/components/TelegramVerificationModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import { FaTelegramPlane, FaCheckCircle, FaExclamationTriangle, FaShieldAlt, FaPhone, FaIdCard } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { fetchTelegramStatus, verifyWidgetPayload, getLocalTelegramData } from '../services/telegramService';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 480 },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'rgba(15, 20, 32, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 136, 204, 0.3)',
  boxShadow: '0 20px 60px rgba(0, 136, 204, 0.25), 0 10px 30px rgba(0,0,0,0.8)',
  borderRadius: 4,
  p: { xs: 2.5, sm: 4 },
  color: '#ffffff',
  outline: 'none',
};

export default function TelegramVerificationModal({ open, onClose, username, onStatusUpdate }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [widgetScriptLoaded, setWidgetScriptLoaded] = useState(false);
  const widgetRef = useRef(null);
  const scriptRef = useRef(null);

  const getSafeUser = () => {
    if (username && username !== 'null' && username !== 'undefined') return username;
    const stored = localStorage.getItem('kick_user');
    if (stored && stored !== 'null' && stored !== 'undefined') return stored;
    return 'Usuario';
  };

  const currentUser = getSafeUser();
  const isVerified = Boolean(status?.verified || status?.isVerified);

  const loadStatus = async () => {
    setLoading(true);
    setErrorMessage(null);
    // Check cached first for immediate rendering
    const cached = getLocalTelegramData(currentUser);
    if (cached) setStatus(cached);

    try {
      const data = await fetchTelegramStatus(currentUser);
      setStatus(data);
      if (data?.error && typeof data.error === 'string') {
        setErrorMessage(data.error);
      } else if (data?.message && !data.verified && !data.isVerified && data.message !== 'El usuario no está verificado aún') {
        setErrorMessage(data.message);
      }
      if (onStatusUpdate) onStatusUpdate(data);
    } catch (err) {
      console.error('Error cargando estado de Telegram:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      if (msg) setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let intervalId = null;

    if (open) {
      loadStatus();

      // Poll status every 3 seconds while modal is open to auto-update when verified
      intervalId = setInterval(async () => {
        try {
          const data = await fetchTelegramStatus(currentUser);
          if (data && (data.verified || data.isVerified)) {
            setStatus(data);
            setErrorMessage(null);
            if (onStatusUpdate) onStatusUpdate(data);
            clearInterval(intervalId);
          } else if (data?.message && !data.verified && !data.isVerified && data.message !== 'El usuario no está verificado aún') {
            setErrorMessage(data.message);
          }
        } catch (err) {
          // ignore transient network errors during poll
        }
      }, 3000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [open, currentUser]);

  // Inyectar el Telegram Login Widget cuando el modal abre y el usuario no está verificado
  useEffect(() => {
    if (!open || loading || isVerified) {
      setWidgetScriptLoaded(false);
      return;
    }

    let checkInterval = null;
    let safetyTimeout = null;

    const timer = setTimeout(() => {
      const container = widgetRef.current;
      if (!container) return;

      container.innerHTML = '';
      setWidgetScriptLoaded(false);

      // Registrar callback global
      window.onTelegramAuthCallback = async (telegramData) => {
        setWidgetLoading(true);
        setErrorMessage(null);
        try {
          const res = await verifyWidgetPayload(telegramData, currentUser);
          if (res && res.success) {
            const statusRes = await fetchTelegramStatus(currentUser, telegramData?.id);
            if (statusRes && (statusRes.verified || statusRes.isVerified)) {
              if (statusRes.username && currentUser && statusRes.username.toLowerCase() !== currentUser.toLowerCase()) {
                setErrorMessage(`Esta cuenta de Telegram ya se encuentra asociada a @${statusRes.username}.`);
              } else {
                setStatus(statusRes);
                if (onStatusUpdate) onStatusUpdate(statusRes);
              }
            } else {
              setErrorMessage(statusRes?.error || statusRes?.message || 'No se pudo confirmar la vinculación. El ID de Telegram podría estar ya asociado a otra cuenta.');
            }
          } else {
            setErrorMessage(res?.error || res?.message || 'Error verificando con Telegram.');
          }
        } catch (err) {
          setErrorMessage(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Error de conexión.');
        } finally {
          setWidgetLoading(false);
        }
      };

      // Inyectar el script del widget
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', 'dng2bot');
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-onauth', 'onTelegramAuthCallback(user)');
      script.setAttribute('data-request-access', 'write');

      script.onload = () => {
        let checks = 0;
        checkInterval = setInterval(() => {
          checks++;
          const iframe = container.querySelector('iframe');
          if (iframe || checks > 25) {
            setWidgetScriptLoaded(true);
            if (checkInterval) clearInterval(checkInterval);
          }
        }, 150);
      };

      script.onerror = () => {
        setWidgetScriptLoaded(true);
      };

      container.appendChild(script);
      scriptRef.current = script;

      safetyTimeout = setTimeout(() => {
        setWidgetScriptLoaded(true);
      }, 3000);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (checkInterval) clearInterval(checkInterval);
      if (safetyTimeout) clearTimeout(safetyTimeout);
      delete window.onTelegramAuthCallback;
    };
  }, [open, currentUser, loading, isVerified]);

  const handleManualCheck = async () => {
    setCheckingStatus(true);
    setErrorMessage(null);
    try {
      const data = await fetchTelegramStatus(currentUser);
      setStatus(data);
      if (data?.error && typeof data.error === 'string') {
        setErrorMessage(data.error);
      } else if (data?.message && !data.verified && !data.isVerified && data.message !== 'El usuario no está verificado aún') {
        setErrorMessage(data.message);
      }
      if (onStatusUpdate) onStatusUpdate(data);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      if (msg) setErrorMessage(msg);
    } finally {
      setCheckingStatus(false);
    }
  };

  const activeError = errorMessage || (status?.error && typeof status.error === 'string' ? status.error : null) || (status?.message && !isVerified && status.message !== 'El usuario no está verificado aún' ? status.message : null);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(0, 136, 204, 0.4)'
              }}
            >
              <FaTelegramPlane size={24} color="#ffffff" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Outfit, sans-serif', lineHeight: 1.2 }}>
                Verificación Telegram
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Verifica tu número y Telegram ID
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: '#9CA3AF', '&:hover': { color: '#FFF' } }}>
            <IoClose size={24} />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

        {loading ? (
          <Box sx={{ textCenter: 'center', py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={40} sx={{ color: '#0088cc' }} />
          </Box>
        ) : isVerified ? (
          /* STATE: VERIFIED */
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(0, 136, 204, 0.08)',
              border: '1px solid rgba(83, 252, 24, 0.3)',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(83, 252, 24, 0.15)',
                color: '#53FC18',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <FaCheckCircle size={36} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFF', mb: 0.5 }}>
              ¡Cuenta Verificada!
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3 }}>
              Tu cuenta de Telegram y número de celular han sido confirmados correctamente.
            </Typography>

            <Stack spacing={1.5} sx={{ textAlign: 'left', bg: 'rgba(0,0,0,0.2)', p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FaIdCard color="#0088cc" />
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>Telegram ID:</Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#FFF' }}>
                  *******
                </Typography>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FaPhone color="#53FC18" />
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>Número Celular:</Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#53FC18' }}>
                  {status?.phoneNumber
                    ? `***${status.phoneNumber.slice(-4)}`
                    : 'Verificado'}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FaShieldAlt color="#0088cc" />
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>Estado Celular:</Typography>
                </Stack>
                <Chip
                  label="Verificado en Telegram"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(83, 252, 24, 0.2)',
                    color: '#53FC18',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    border: '1px solid rgba(83, 252, 24, 0.4)'
                  }}
                />
              </Box>
            </Stack>

            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{
                mt: 3,
                borderColor: '#0088cc',
                color: '#0088cc',
                borderRadius: '12px',
                py: 1.2,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { borderColor: '#229ED9', bgcolor: 'rgba(0,136,204,0.1)' }
              }}
            >
              Cerrar
            </Button>
          </Paper>
        ) : (
          /* STATE: NOT VERIFIED YET */
          <Box>
            {activeError && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  mb: 3,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <FaExclamationTriangle color="#EF4444" size={22} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#EF4444' }}>
                      Error de Verificación
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FCA5A5', fontSize: '0.85rem', lineHeight: 1.4, mt: 0.5 }}>
                      {activeError}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: 'rgba(255, 170, 0, 0.08)',
                border: '1px solid rgba(255, 170, 0, 0.3)',
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <FaExclamationTriangle color="#FFAA00" size={20} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFAA00' }}>
                  Verificación Pendiente
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#D1D5DB', lineHeight: 1.5 }}>
                Conectá tu cuenta oficial de Telegram haciendo clic en el botón a continuación para verificar tu identidad.
              </Typography>
            </Paper>

            {/* Instruction Steps */}
            <Typography variant="caption" sx={{ color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, mb: 1.5, display: 'block' }}>
              Pasos para verificar:
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#0088cc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  1
                </Box>
                <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                  Hacé clic en el botón azul <b>Log in with Telegram</b>.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#0088cc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  2
                </Box>
                <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                  Autorizá el acceso con tu cuenta de Telegram.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#0088cc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  3
                </Box>
                <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                  ¡Listo! Tu cuenta quedará verificada al instante.
                </Typography>
              </Box>
            </Stack>

            {/* Telegram Login Widget oficial */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: 'rgba(0, 136, 204, 0.05)',
                border: '1px solid rgba(0, 136, 204, 0.2)',
                textAlign: 'center',
                mb: 2,
              }}
            >
              <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mb: 1.5 }}>
                Hacé clic en el botón de Telegram para autenticarte:
              </Typography>
              {widgetLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={32} sx={{ color: '#53FC18' }} />
                  <Typography variant="caption" sx={{ color: '#53FC18', fontWeight: 600 }}>
                    Verificando cuenta...
                  </Typography>
                </Box>
              ) : (
                <>
                  {!widgetScriptLoaded && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={24} sx={{ color: '#0088cc' }} />
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        Cargando botón de Telegram...
                      </Typography>
                    </Box>
                  )}
                  <Box
                    ref={widgetRef}
                    sx={{
                      display: widgetScriptLoaded ? 'flex' : 'none',
                      justifyContent: 'center',
                      minHeight: 44,
                      width: '100%',
                    }}
                  />
                </>
              )}
            </Box>

            {/* Comprobar estado manual */}
            {/* <Button
              fullWidth
              variant="contained"
              onClick={handleManualCheck}
              disabled={checkingStatus}
              startIcon={checkingStatus ? <CircularProgress size={16} color="inherit" /> : <FaCheckCircle />}
              sx={{
                py: 1.2,
                bgcolor: 'rgba(255,255,255,0.08)',
                color: '#FFF',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
              }}
            >
              Comprobar Estado
            </Button> */}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
